import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";

import FlightRefundService from "@/modules/FlightRefunds/services/flightRefunds.service";
import {
  ICoreResponse,
  ICoreErrorResponse,
} from "@/core/interfaces/coreResponse.interface";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import {
  CalculateFlightRefundPNRPayload,
  FetchFlightRefundPNRPayload,
  CommitFlightRefundPNRPayload,
  ActiveProvidersAndAirlinesRefundApiResponse,
} from "@/core/interfaces/IFlightRefunds.interface";
import notificationService from "@/core/services/notification.service";
import { IFlightRefundState } from "@/core/interfaces/IFlightRefunds.interface";
import { StoreState } from "@/store/type";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";

const actions: ActionTree<IFlightRefundState, StoreState> = {
  // ****** Fetch Refund PNR ******
  async fetchRefundPNR(
    context: ActionContext<IFlightRefundState, StoreState>,
    params: FetchFlightRefundPNRPayload,
  ) {
    context.commit("resetPNRDetails");

    try {
      context.commit("isPNRFetching", true);

      const flightRefundService = new FlightRefundService();
      const response = await flightRefundService.fetchRefundPNR(params);

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        response.data?.data
      ) {
        context.commit("fetchedPNRDetail", response.data.data);
      } else {
        throw response;
      }
    } catch (error: any) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description = error?.message || "";
      notificationService.triggerNotification();
    } finally {
      context.commit("isPNRFetching", false);
    }
  },
  async calculateRefundPNR(
    context: ActionContext<IFlightRefundState, StoreState>,
    params: CalculateFlightRefundPNRPayload,
  ) {
    try {
      context.commit("fetchingCalculateRefundPNR", true);

      const flightRefundService = new FlightRefundService();
      const response = await flightRefundService.calculateRefundPNR(params);

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        response.data?.data
      ) {
        context.commit("calculatePNRDetail", response.data.data);
      } else {
        throw response;
      }
    } catch (error: any) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description = error?.message || "";
      notificationService.triggerNotification();
    } finally {
      context.commit("fetchingCalculateRefundPNR", false);
    }
  },
  async commitRefundPNR(
    context: ActionContext<IFlightRefundState, StoreState>,
    params: CommitFlightRefundPNRPayload,
  ) {
    try {
      context.commit("fetchingCommitRefundPNR", true);

      const flightRefundService = new FlightRefundService();
      const response: ICoreResponse =
        await flightRefundService.commitRefundPNR(params);

      if (response.success && response.status === StatusCodes.OK) {
        context.commit("commitPNRDetail", response.data.data);
        context.commit("refundedDetails");

        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = "Refunded Successfully";
        notificationService.triggerNotification();

        context.commit("resetRefundPNRDetails");
      } else {
        throw response;
      }
    } catch (error: any) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description = error?.message || "";
      notificationService.triggerNotification();
    } finally {
      context.commit("fetchingCommitRefundPNR", false);
    }
  },
  // FETCH REFUND AIRLINES AND SABRE PROVIDERS
  async fetchRefundAirlinesAndProviders(
    context: ActionContext<IFlightRefundState, StoreState>,
    organization_id: string,
  ) {
    try {
      const flightRefundService = new FlightRefundService();
      const response: ICoreResponse =
        await flightRefundService.getActiveRefundProviders(organization_id);

      context.commit("isRefundAirlineAndProviderFetching", true);
      if (response.success && response.status === StatusCodes.OK) {
        const responseData: ActiveProvidersAndAirlinesRefundApiResponse =
          response.data;
        context.commit("addRefundActiveAirlinesAndProviders", responseData);
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;

      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      notificationService.triggerNotification();
    } finally {
      context.commit("isRefundAirlineAndProviderFetching", false);
    }
  },
};

export default actions;
